import { useState, useCallback, useEffect } from "react";
import { BillCountsEntity, GetBillCountsUseCase } from "../../domain";
import { BillRepositoryImpl } from "../../data/repositories/BillRepositoryImpl";

// Create repository instance (singleton)
const billRepository = new BillRepositoryImpl();

// Create use case
const getBillCountsUseCase = new GetBillCountsUseCase(billRepository);

export interface UseBillCountsReturn {
  counts: BillCountsEntity | null;
  loading: boolean;
  error: string | null;
  fetchCounts: (params?: {
    date_from?: string;
    date_to?: string;
  }) => Promise<void>;
  clearError: () => void;
}

export const useBillCounts = (initialParams?: {
  date_from?: string;
  date_to?: string;
}): UseBillCountsReturn => {
  const [counts, setCounts] = useState<BillCountsEntity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch bill counts
   */
  const fetchCounts = useCallback(async (params?: {
    date_from?: string;
    date_to?: string;
  }): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const result = await getBillCountsUseCase.execute(params);
      setCounts(result);
    } catch (error: any) {
      const errorMessage = error.message || "Failed to load bill counts";
      setError(errorMessage);
      console.error("Failed to fetch bill counts:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  // Load initial data on mount
  useEffect(() => {
    fetchCounts(initialParams);
  }, [fetchCounts, initialParams]);

  return {
    counts,
    loading,
    error,
    fetchCounts,
    clearError,
  };
};

